import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';

function CompanyShowcase() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const formatPhone = (phone) => {
    if (!phone) return null;
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 10) {
      return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
    }
    return phone;
  };

  useEffect(() => {
    const loadCompany = async () => {
      const apiUrl = process.env.REACT_APP_API_URL;
      try {
        const response = await axios.get(`${apiUrl}/api/public/company/${slug}?product=lawn`);
        setData(response.data);
        setError('');
      } catch (err) {
        if (err.response?.status === 404) {
          setError('Company not found');
        } else {
          setError('Unable to load company information');
        }
      } finally {
        setLoading(false);
      }
    };

    if (slug) loadCompany();
  }, [slug]);

  if (loading) {
    return (
      <>
        <style>{styles}</style>
        <div className="showcase-page">
          <div className="showcase-loading">
            <div className="loading-spinner" />
            <p>Loading...</p>
          </div>
        </div>
      </>
    );
  }

  if (error || !data) {
    const isNotFound = error === 'Company not found';
    return (
      <>
        <style>{styles}</style>
        <div className="showcase-page">
          <div className="showcase-error">
            <h1>{isNotFound ? 'Company Not Found' : 'Something Went Wrong'}</h1>
            <p>{isNotFound
              ? "The company page you're looking for doesn't exist or has been removed."
              : 'We had trouble loading this page. Please try again in a moment.'}</p>
            <a href="https://lawntrack.pro" className="btn-cta">Visit LawnTrack</a>
          </div>
        </div>
      </>
    );
  }

  const { company, pricing_tiers, service_addons } = data;
  const signupUrl = `/new-customer/${slug}`;
  const yearFounded = company.member_since ? new Date(company.member_since).getFullYear() : null;
  const currentYear = new Date().getFullYear();
  const yearsInBusiness = yearFounded ? currentYear - yearFounded : null;

  return (
    <>
      <style>{styles}</style>
      <div className="showcase-page">

        {/* Hero Section */}
        <section className="showcase-hero">
          <div className="hero-content">
            <h1 className="hero-title">{company.name}</h1>
            {company.tagline && (
              <p className="hero-tagline">{company.tagline}</p>
            )}
            <Link to={signupUrl} className="btn-cta btn-cta-large">
              Get a Free Quote
            </Link>
          </div>
        </section>

        {/* Services & Pricing Section */}
        {company.show_pricing && pricing_tiers.length > 0 && (
          <section className="showcase-section">
            <div className="section-container">
              <h2 className="section-title">Services & Pricing</h2>
              <p className="section-subtitle">Professional lawn care at competitive rates</p>

              <div className="pricing-grid">
                {pricing_tiers.map((tier, i) => {
                  const basePrice = tier.default_price;
                  const discount = company.seasonal_discount_percent > 0
                    ? basePrice * (1 - company.seasonal_discount_percent / 100)
                    : company.seasonal_discount_amount > 0
                      ? Math.max(0, basePrice - company.seasonal_discount_amount)
                      : null;
                  const hasDiscount = discount !== null && discount !== basePrice;

                  return (
                    <div key={i} className={`pricing-card${i === Math.floor(pricing_tiers.length / 2) ? ' pricing-card-featured' : ''}`}>
                      <h3 className="pricing-tier-name">{tier.name}</h3>
                      {hasDiscount ? (
                        <>
                          <div className="pricing-amount">
                            <span className="pricing-label">Seasonal</span>
                            <span className="pricing-dollar">$</span>
                            <span className="pricing-value">{discount.toFixed(0)}</span>
                            <span className="pricing-period">/visit</span>
                          </div>
                          <div className="pricing-amount pricing-oncall">
                            <span className="pricing-label">On-Call</span>
                            <span className="pricing-dollar">$</span>
                            <span className="pricing-value">{basePrice}</span>
                            <span className="pricing-period">/visit</span>
                          </div>
                        </>
                      ) : (
                        <div className="pricing-amount">
                          <span className="pricing-dollar">$</span>
                          <span className="pricing-value">{basePrice}</span>
                          <span className="pricing-period">/visit</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {company.standard_services && (
                <div className="services-included">
                  <h3>Standard Services Included</h3>
                  <div className="services-list">
                    {company.standard_services.split(',').map(service => (
                      <span key={service} className="service-badge">
                        {service.charAt(0).toUpperCase() + service.slice(1)}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Add-on Services */}
        {company.show_pricing && service_addons.length > 0 && (
          <section className="showcase-section showcase-section-alt">
            <div className="section-container">
              <h2 className="section-title">Add-On Services</h2>
              <p className="section-subtitle">Customize your lawn care package</p>

              <div className="addons-grid">
                {service_addons.map((addon, i) => (
                  <div key={i} className="addon-card">
                    <span className="addon-name">{addon.name}</span>
                    <span className="addon-price">from ${addon.price}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* About Section */}
        <section className="showcase-section">
          <div className="section-container">
            <h2 className="section-title">About Us</h2>

            {company.description && (
              <p className="about-description">{company.description}</p>
            )}

            <div className="about-details">
              {(company.city || company.state) && (
                <div className="about-item">
                  <span className="about-icon">📍</span>
                  <div>
                    <strong>Service Area</strong>
                    <p>{[company.city, company.state].filter(Boolean).join(', ')}</p>
                  </div>
                </div>
              )}
              {yearsInBusiness !== null && yearsInBusiness > 0 && (
                <div className="about-item">
                  <span className="about-icon">📅</span>
                  <div>
                    <strong>Experience</strong>
                    <p>{yearsInBusiness} year{yearsInBusiness !== 1 ? 's' : ''} in business</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        {(company.phone || company.email) && (
          <section className="showcase-section showcase-section-alt">
            <div className="section-container">
              <h2 className="section-title">Get In Touch</h2>

              <div className="contact-grid">
                {company.phone && (
                  <a href={`tel:${company.phone}`} className="contact-card">
                    <span className="contact-icon">📞</span>
                    <strong>Call Us</strong>
                    <span>{formatPhone(company.phone)}</span>
                  </a>
                )}
                {company.email && (
                  <a href={`mailto:${company.email}`} className="contact-card">
                    <span className="contact-icon">📧</span>
                    <strong>Email Us</strong>
                    <span>{company.email}</span>
                  </a>
                )}
                {(company.city || company.state) && (
                  <div className="contact-card">
                    <span className="contact-icon">📍</span>
                    <strong>Service Area</strong>
                    <span>{[company.city, company.state].filter(Boolean).join(', ')}</span>
                  </div>
                )}
              </div>

              <div className="contact-cta">
                <Link to={signupUrl} className="btn-cta btn-cta-large">
                  Sign Up for Service
                </Link>
              </div>
            </div>
          </section>
        )}

        {/* Footer */}
        <footer className="showcase-footer">
          <Link to={signupUrl} className="btn-cta btn-cta-outline">
            Get Started Today
          </Link>
          <p className="footer-powered">
            Powered by <a href="https://lawntrack.pro" target="_blank" rel="noopener noreferrer">LawnTrack</a>
          </p>
        </footer>
      </div>
    </>
  );
}

const styles = `
  .showcase-page {
    min-height: 100vh;
    background: #f9fafb;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    color: #1f2937;
  }

  /* Hero */
  .showcase-hero {
    background: linear-gradient(135deg, #1b5e20 0%, #4CAF50 50%, #81C784 100%);
    padding: 80px 20px;
    text-align: center;
  }

  .hero-content {
    max-width: 700px;
    margin: 0 auto;
  }

  .hero-title {
    font-size: 2.75rem;
    font-weight: 800;
    color: white;
    margin: 0 0 12px;
    text-shadow: 0 2px 4px rgba(0,0,0,0.2);
  }

  .hero-tagline {
    font-size: 1.25rem;
    color: rgba(255,255,255,0.9);
    margin: 0 0 32px;
    line-height: 1.5;
  }

  /* Buttons */
  .btn-cta {
    display: inline-block;
    background: #ff9800;
    color: white;
    padding: 14px 36px;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 700;
    text-decoration: none;
    transition: background 0.2s, transform 0.2s;
    border: none;
    cursor: pointer;
  }

  .btn-cta:hover {
    background: #f57c00;
    transform: translateY(-1px);
  }

  .btn-cta-large {
    padding: 16px 44px;
    font-size: 1.125rem;
  }

  .btn-cta-outline {
    background: transparent;
    border: 2px solid #4CAF50;
    color: #4CAF50;
  }

  .btn-cta-outline:hover {
    background: #4CAF50;
    color: white;
  }

  /* Sections */
  .showcase-section {
    padding: 60px 20px;
  }

  .showcase-section-alt {
    background: #f0f7f0;
  }

  .section-container {
    max-width: 800px;
    margin: 0 auto;
  }

  .section-title {
    font-size: 1.75rem;
    font-weight: 700;
    color: #1b5e20;
    text-align: center;
    margin: 0 0 8px;
  }

  .section-subtitle {
    text-align: center;
    color: #6b7280;
    margin: 0 0 36px;
    font-size: 1.05rem;
  }

  /* Pricing Cards */
  .pricing-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 20px;
    margin-bottom: 36px;
  }

  .pricing-card {
    background: white;
    border-radius: 12px;
    padding: 28px 20px;
    text-align: center;
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
    border: 2px solid transparent;
    transition: transform 0.2s, box-shadow 0.2s;
  }

  .pricing-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(0,0,0,0.12);
  }

  .pricing-card-featured {
    border-color: #4CAF50;
    box-shadow: 0 4px 16px rgba(76,175,80,0.2);
  }

  .pricing-tier-name {
    font-size: 1rem;
    font-weight: 600;
    color: #374151;
    margin: 0 0 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .pricing-amount {
    display: flex;
    align-items: baseline;
    justify-content: center;
    gap: 2px;
  }

  .pricing-dollar {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1b5e20;
  }

  .pricing-value {
    font-size: 2.5rem;
    font-weight: 800;
    color: #1b5e20;
    line-height: 1;
  }

  .pricing-period {
    font-size: 0.9rem;
    color: #6b7280;
    margin-left: 2px;
  }

  .pricing-label {
    display: block;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #4CAF50;
    font-weight: 600;
    margin-bottom: 2px;
  }

  .pricing-oncall {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid #e0e0e0;
    opacity: 0.75;
  }

  .pricing-oncall .pricing-dollar,
  .pricing-oncall .pricing-value {
    font-size: 1.2rem;
  }

  .pricing-oncall .pricing-label {
    color: #666;
  }

  /* Services Included */
  .services-included {
    text-align: center;
  }

  .services-included h3 {
    font-size: 1rem;
    font-weight: 600;
    color: #374151;
    margin: 0 0 12px;
  }

  .services-list {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 10px;
  }

  .service-badge {
    background: #e8f5e9;
    color: #2e7d32;
    padding: 8px 18px;
    border-radius: 20px;
    font-size: 0.9rem;
    font-weight: 600;
  }

  /* Add-ons */
  .addons-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 12px;
  }

  .addon-card {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: white;
    padding: 16px 20px;
    border-radius: 8px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  }

  .addon-name {
    font-weight: 600;
    color: #374151;
  }

  .addon-price {
    color: #2e7d32;
    font-weight: 700;
    white-space: nowrap;
  }

  /* About */
  .about-description {
    font-size: 1.1rem;
    line-height: 1.7;
    color: #4b5563;
    text-align: center;
    margin: 0 0 32px;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
  }

  .about-details {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 32px;
  }

  .about-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }

  .about-icon {
    font-size: 1.5rem;
    line-height: 1;
  }

  .about-item strong {
    display: block;
    font-size: 0.85rem;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 2px;
  }

  .about-item p {
    margin: 0;
    font-size: 1rem;
    color: #1f2937;
    font-weight: 600;
  }

  /* Contact */
  .contact-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
    margin-bottom: 32px;
  }

  .contact-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    background: white;
    padding: 24px 20px;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
    text-decoration: none;
    color: #1f2937;
    transition: transform 0.2s, box-shadow 0.2s;
  }

  a.contact-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0,0,0,0.12);
  }

  .contact-icon {
    font-size: 1.75rem;
  }

  .contact-card strong {
    font-size: 0.85rem;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .contact-card span:last-child {
    color: #2e7d32;
    font-weight: 600;
  }

  .contact-cta {
    text-align: center;
  }

  /* Footer */
  .showcase-footer {
    padding: 48px 20px;
    text-align: center;
    background: #1b5e20;
  }

  .footer-powered {
    margin: 20px 0 0;
    font-size: 0.85rem;
    color: rgba(255,255,255,0.6);
  }

  .footer-powered a {
    color: rgba(255,255,255,0.85);
    text-decoration: none;
    font-weight: 600;
  }

  .footer-powered a:hover {
    color: white;
  }

  /* Loading / Error */
  .showcase-loading, .showcase-error {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    text-align: center;
    padding: 20px;
  }

  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #e5e7eb;
    border-top-color: #4CAF50;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin-bottom: 16px;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .showcase-error h1 {
    color: #1b5e20;
    margin: 0 0 8px;
  }

  .showcase-error p {
    color: #6b7280;
    margin: 0 0 24px;
  }

  /* Mobile */
  @media (max-width: 640px) {
    .showcase-hero {
      padding: 60px 20px;
    }

    .hero-title {
      font-size: 2rem;
    }

    .hero-tagline {
      font-size: 1.05rem;
    }

    .showcase-section {
      padding: 40px 16px;
    }

    .pricing-grid {
      grid-template-columns: 1fr;
    }

    .addons-grid {
      grid-template-columns: 1fr;
    }

    .contact-grid {
      grid-template-columns: 1fr;
    }

    .btn-cta-large {
      padding: 14px 32px;
      font-size: 1rem;
    }
  }
`;

export default CompanyShowcase;
